import { makeAutoObservable, observable, computed } from "mobx";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { WhatsappBusinessApiHeaderType } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";

type ButtonItem = { type: "QUICK_REPLY" | "URL"; text: string; url: string };

export const WABA_TEMPLATE_LIMITS = { headerText: 60, body: 1024, footer: 60, buttonText: 25 };

export type TemplateFieldError = { key: string; values?: Record<string, string | number> };

const tooLong = (value: string, max: number): TemplateFieldError | undefined =>
  value.length > max ? { key: "too_long", values: { max } } : undefined;
// Braces may only appear as {{n}} -- catches {1}, {{ 1 }}, {{name}}, {{1 and 1}}
const hasMalformedVariable = (text: string) => /[{}]/.test(text.replace(/\{\{\d+\}\}/g, ""));
const isHttpUrl = (value: string) => {
  try {
    return ["http:", "https:"].includes(new URL(value).protocol);
  } catch {
    return false;
  }
};

export class CreateTemplateModalStore {
  name = "";
  wabaOption: { label: string; value: string } | undefined = undefined;
  category = "UTILITY";
  language = "en_US";
  headerType: WhatsappBusinessApiHeaderType = "NONE";
  headerText = "";
  bodyText = "";
  bodyParameterSamples: Record<number, string> = {};
  footerText = "";
  buttons: ButtonItem[] = [];
  isSubmitting = false;
  headerMediaFile: File | null = null;
  headerMediaUpload: IPromiseBasedObservable<{ handle: string }> | null = null;

  constructor() {
    makeAutoObservable(this, {
      buttons: observable.shallow,
      fieldErrors: computed,
      headerMediaFile: observable.ref,
      headerMediaUpload: observable.ref,
    });
  }

  setName(name: string) {
    this.name = name;
  }

  setWabaOption(value: { label: string; value: string }) {
    this.wabaOption = value;
  }

  setCategory(category: string) {
    this.category = category;
  }

  setLanguage(language: string) {
    this.language = language;
  }

  setHeaderType(type: WhatsappBusinessApiHeaderType) {
    this.headerType = type;
    this.headerText = "";
    this.clearHeaderMedia();
  }

  setHeaderText(text: string) {
    this.headerText = text;
  }

  setBodyText(text: string) {
    this.bodyText = text;
  }

  setFooterText(text: string) {
    this.footerText = text;
  }

  setParameterSample(n: number, value: string) {
    this.bodyParameterSamples = { ...this.bodyParameterSamples, [n]: value };
  }

  setHeaderMediaFile(file: File | null) {
    this.headerMediaFile = file;
    if (!file) {
      this.headerMediaUpload = null;
    }
  }

  clearHeaderMedia() {
    this.headerMediaFile = null;
    this.headerMediaUpload = null;
  }

  uploadHeaderMedia(upload: (wabaId: string, file: File) => Promise<{ handle: string }>, file: File) {
    this.headerMediaFile = file;

    if (!this.wabaOption) {
      this.headerMediaUpload = null;
      return;
    }

    this.headerMediaUpload = fromPromise(upload(this.wabaOption.value, file));
  }

  addButton() {
    if (this.buttons.length >= 3) return;
    this.buttons = [...this.buttons, { type: "QUICK_REPLY", text: "", url: "" }];
  }

  removeButton(index: number) {
    this.buttons = this.buttons.filter((_, i) => i !== index);
  }

  updateButton(index: number, field: keyof ButtonItem, value: string) {
    this.buttons = this.buttons.map((btn, i) => (i === index ? { ...btn, [field]: value } : btn));
  }

  get isMediaHeaderType(): boolean {
    return this.headerType === "IMAGE" || this.headerType === "VIDEO" || this.headerType === "DOCUMENT";
  }

  get detectedParameters(): number[] {
    const matches = this.bodyText.matchAll(/\{\{(\d+)\}\}/g);
    const nums = new Set<number>();
    for (const match of matches) {
      nums.add(Number(match[1]));
    }
    return [...nums].sort((a, b) => a - b);
  }

  // Errors for what the user typed; empty required fields only keep the submit button disabled
  get fieldErrors() {
    return {
      name: this.name && !/^[a-z0-9_]+$/.test(this.name) ? { key: "name_invalid" } : undefined,
      header: this.headerType === "TEXT" ? tooLong(this.headerText, WABA_TEMPLATE_LIMITS.headerText) : undefined,
      body: this.bodyError,
      footer: tooLong(this.footerText, WABA_TEMPLATE_LIMITS.footer),
      buttons: this.buttons.map((btn) => tooLong(btn.text, WABA_TEMPLATE_LIMITS.buttonText)),
      buttonUrls: this.buttons.map((btn) => (btn.type === "URL" && btn.url && !isHttpUrl(btn.url) ? { key: "invalid_url" } : undefined)),
    };
  }

  private get bodyError(): TemplateFieldError | undefined {
    if (hasMalformedVariable(this.bodyText)) return { key: "variable_format", values: { example: "{{1}}" } };
    // Meta rejects variables that don't run {{1}}, {{2}}, ... without gaps
    if (this.detectedParameters.some((n, i) => n !== i + 1)) return { key: "variables_not_sequential", values: { example: "{{1}}, {{2}}" } };
    return tooLong(this.bodyText, WABA_TEMPLATE_LIMITS.body);
  }

  get isValidInput(): boolean {
    if (!/^[a-z0-9_]+$/.test(this.name)) return false;
    if (!this.wabaOption) return false;
    if (!this.category) return false;
    if (!this.language) return false;
    if (!this.bodyText.trim()) return false;
    if (this.headerType === "TEXT" && !this.headerText.trim()) return false;
    if (this.isMediaHeaderType && this.headerMediaUpload?.state !== "fulfilled") return false;
    const { buttons, buttonUrls, ...fieldErrors } = this.fieldErrors;
    if (Object.values(fieldErrors).some(Boolean) || buttons.some(Boolean) || buttonUrls.some(Boolean)) return false;
    for (const param of this.detectedParameters) {
      if (!this.bodyParameterSamples[param]?.trim()) return false;
    }
    for (const btn of this.buttons) {
      if (!btn.text.trim()) return false;
      if (btn.type === "URL" && !btn.url.trim()) return false;
    }
    return true;
  }

  get submitData() {
    const components: any[] = [];

    if (this.headerType === "TEXT" && this.headerText) {
      components.push({
        type: "HEADER",
        format: "TEXT",
        text: this.headerText,
      });
    } else if (this.isMediaHeaderType && this.headerMediaUpload?.state === "fulfilled") {
      components.push({
        type: "HEADER",
        format: this.headerType,
        example: { header_handle: [this.headerMediaUpload.value.handle] },
      });
    }

    const bodyComponent: any = { type: "BODY", text: this.bodyText };
    if (this.detectedParameters.length > 0) {
      bodyComponent.example = {
        body_text: [this.detectedParameters.map((n) => this.bodyParameterSamples[n] || `{{${n}}}`)],
      };
    }
    components.push(bodyComponent);

    if (this.footerText) {
      components.push({ type: "FOOTER", text: this.footerText });
    }

    if (this.buttons.length > 0) {
      components.push({
        type: "BUTTONS",
        buttons: this.buttons.map((btn) =>
          btn.type === "QUICK_REPLY" ? { type: "QUICK_REPLY", text: btn.text } : { type: "URL", text: btn.text, url: btn.url }
        ),
      });
    }

    return {
      wabaId: this.wabaOption!.value,
      name: this.name,
      language: this.language,
      category: this.category,
      components,
    };
  }

  get previewData() {
    let bodyTextWithSamples = this.bodyText;
    for (const n of this.detectedParameters) {
      const sample = this.bodyParameterSamples[n];
      if (sample) {
        bodyTextWithSamples = bodyTextWithSamples.replace(new RegExp(`\\{\\{${n}\\}\\}`, "g"), sample);
      }
    }

    return {
      headerText: this.headerType === "TEXT" ? this.headerText : "",
      headerMediaType: this.isMediaHeaderType ? this.headerType : undefined,
      bodyTextWithSamples,
      footerText: this.footerText,
      buttons: this.buttons,
    };
  }
}
